import '@fontsource-variable/inter'

import { CssBaseline, ThemeProvider } from '@mui/material'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'

import { reloadOnStaleBuild } from './lib/staleBuild'
import { router } from './router'
import { theme } from './theme'

reloadOnStaleBuild()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 or a 404 will not become a 200 by asking again — retrying them
      // only makes a signed-out user wait three times as long to be told.
      retry: (failures, error) => {
        const status = (error as { status?: number }).status
        if (status && status < 500) return false
        return failures < 2
      },
      // Coming back to the tab is exactly when somebody expects to see what
      // changed while they were away — and on this product most of what
      // changes is done BY SOMEBODY ELSE. Somebody added to a campaign while
      // their dashboard sat open saw nothing until they signed out and back
      // in, because a remount was the only thing that refetched.
      //
      // `staleTime` is the throttle that makes it safe: a refocus inside
      // thirty seconds still serves the cache, so flicking between tabs costs
      // nothing.
      refetchOnWindowFocus: true,
      staleTime: 30_000,
    },
  },
})

/**
 * A page restored by the back button re-asks who is signed in.
 *
 * Signing out clears the session properly — every API route answers 401
 * afterwards — but the browser keeps the whole rendered page in its
 * back/forward cache and hands it back intact, React state and all. Nobody
 * could act on it, and the campaign names, amounts and roster were on screen
 * anyway, which on a shared machine is the part that matters.
 *
 * `persisted` is true only for a bfcache restore, so an ordinary navigation
 * pays nothing. Invalidating rather than reloading keeps the back button
 * quick for somebody still signed in: their queries refetch, the guard sees a
 * live session, and nothing moves.
 */
window.addEventListener('pageshow', (event) => {
  if ((event as PageTransitionEvent).persisted) {
    void queryClient.invalidateQueries()
  }
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <RouterProvider router={router} />
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
)
